/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * callSlice.ts — cal\1 \2lice (services/operator-web-app).
 */

export type CallPhase = 'idle' | 'offered' | 'assigned' | 'connected' | 'ended';

export interface CallOffer {
  queueId: string;
  from: string;
  to: string;
  offeredAt: string;
  timeoutMs: number;
  priority?: number;
}

export interface CallState {
  phase: CallPhase;
  callId: string | null;
  sessionId: string | null;
  offer: CallOffer | null;
  connectedAt: string | null;
  endedAt: string | null;
  durationMs: number | null;
  endReason: string | null;
  lastEventId: string | null;
}

export const initialCallState: CallState = {
  phase: 'idle',
  callId: null,
  sessionId: null,
  offer: null,
  connectedAt: null,
  endedAt: null,
  durationMs: null,
  endReason: null,
  lastEventId: null,
};

export function reduceCall(
  state: CallState,
  eventType: string,
  payload: Record<string, unknown>,
  eventId: string,
  callId?: string,
  sessionId?: string
): CallState {
  switch (eventType) {
    case 'call_offered': {
      const p = payload as {
        queue_id: string;
        from: string;
        to: string;
        offered_at: string;
        timeout_ms: number;
        priority?: number;
      };
      return {
        ...initialCallState,
        phase: 'offered',
        callId: callId ?? state.callId,
        sessionId: sessionId ?? state.sessionId,
        offer: {
          queueId: p.queue_id,
          from: p.from,
          to: p.to,
          offeredAt: p.offered_at,
          timeoutMs: p.timeout_ms,
          priority: p.priority,
        },
        lastEventId: eventId,
      };
    }
    case 'call_assigned':
      return {
        ...state,
        phase: 'assigned',
        callId: callId ?? state.callId,
        sessionId: sessionId ?? state.sessionId,
        lastEventId: eventId,
      };
    case 'call_connected': {
      const p = payload as { connected_at: string };
      return {
        ...state,
        phase: 'connected',
        connectedAt: p.connected_at,
        lastEventId: eventId,
      };
    }
    case 'call_ended': {
      const p = payload as { ended_at: string; duration_ms: number; end_reason: string };
      return {
        ...state,
        phase: 'ended',
        endedAt: p.ended_at,
        durationMs: p.duration_ms,
        endReason: p.end_reason,
        lastEventId: eventId,
      };
    }
    default:
      return state;
  }
}
